import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { sendQuickActionPaymentLink } from './patientQuickActionsService'

// Create-and-send an ad-hoc payment link. Email always goes out; WhatsApp only if the 24h window
// is open. The created link is always shown so the mentor can copy/share it regardless.
export default function SendPaymentLinkDialog({ open, onClose, contactContext, patientName, onSent }) {
  const isSupport = contactContext?.source === 'support-patient'
  const [amount, setAmount] = useState('')
  const [purpose, setPurpose] = useState('')
  const [fromName, setFromName] = useState('')
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState(null)
  const resolvedName = patientName || 'the patient'

  useEffect(() => {
    if (open) {
      setAmount('')
      setPurpose('')
      // Support chooses the "from" name shown to the patient — default to the clinic brand.
      setFromName(isSupport ? contactContext?.clinicBrandName || '' : '')
      setResult(null)
      setSending(false)
    }
  }, [open, isSupport, contactContext?.clinicBrandName])

  const numericAmount = Number(amount)
  const amountValid = amount !== '' && Number.isFinite(numericAmount) && numericAmount > 0

  const handleSend = useCallback(async () => {
    if (!amountValid) {
      toast.error('Enter a valid amount.')
      return
    }
    setSending(true)
    const response = await sendQuickActionPaymentLink(
      contactContext,
      numericAmount,
      purpose.trim(),
      isSupport ? fromName.trim() : undefined,
    )
    setSending(false)
    if (response?.ok) {
      setResult(response)
      const channels = []
      if (response.emailSent) channels.push('email')
      if (response.whatsappSent) channels.push('WhatsApp')
      toast.success(channels.length ? `Payment link sent via ${channels.join(' & ')}.` : 'Payment link created.')
      if (response.whatsappSent || response.emailSent) {
        onSent?.()
      }
    } else {
      toast.error(response?.message || 'Unable to create the payment link.')
    }
  }, [amountValid, numericAmount, purpose, fromName, isSupport, contactContext, onSent])

  const handleCopy = useCallback(async () => {
    if (!result?.paymentLink) return
    try {
      await navigator.clipboard.writeText(result.paymentLink)
      toast.success('Payment link copied.')
    } catch (e) {
      toast.error('Could not copy the link.')
    }
  }, [result])

  return (
    <Dialog open={!!open} onClose={sending ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <PaymentOutlinedIcon sx={{ color: '#4338ca' }} />
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.2 }}>
            Send payment link
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            to {resolvedName}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={sending}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          aria-label="Close"
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {!result ? (
          <Stack spacing={2}>
            <TextField
              label="Amount"
              type="number"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              fullWidth
              autoFocus
              disabled={sending}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
              inputProps={{ min: 1, step: '1' }}
            />
            <TextField
              label="Purpose (optional)"
              placeholder="e.g. Consultation fee"
              value={purpose}
              onChange={(event) => setPurpose(event.target.value)}
              fullWidth
              disabled={sending}
            />
            {isSupport && (
              <TextField
                label="Send from name"
                placeholder="e.g. the clinic name"
                helperText="Shown to the patient as who the payment link is from."
                value={fromName}
                onChange={(event) => setFromName(event.target.value)}
                fullWidth
                disabled={sending}
              />
            )}
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              A secure Razorpay link is created and sent over email (and WhatsApp if the chat window is
              open). You&apos;ll also get the link here to copy or share.
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleRoundedIcon sx={{ color: '#16a34a' }} />
              <Typography sx={{ fontWeight: 600 }}>Payment link ready</Typography>
            </Box>
            <TextField
              label="Payment link"
              value={result.paymentLink}
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Copy link">
                      <IconButton onClick={handleCopy} edge="end" size="small">
                        <ContentCopyRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />
            <Alert severity={result.whatsappSent || result.emailSent ? 'success' : 'info'}>
              {result.emailSent ? 'Emailed to the patient. ' : ''}
              {result.whatsappSent
                ? 'Sent on WhatsApp. '
                : 'Not sent on WhatsApp (the 24-hour window is closed) — copy the link above to share. '}
            </Alert>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {!result ? (
          <>
            <Button onClick={onClose} disabled={sending} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={sending || !amountValid}
              startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendRoundedIcon />}
              sx={{ textTransform: 'none', bgcolor: '#4338ca', '&:hover': { bgcolor: '#3730a3' } }}
            >
              {sending ? 'Creating…' : 'Create & send'}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={onClose} sx={{ textTransform: 'none' }}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
